import { Box, Typography } from '@mui/material';
import { CustomCellEditorProps } from 'ag-grid-react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ColDef } from 'ag-grid-community';
import { EventLogApiModel, WithUser } from '../../../types/models/event-log.model';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { searchEventLogs } from '../api/event-logs.api';
import { ArrayFilter } from '../components/AgGrid/ArrayFilter';
import { EventType } from '../../../types/constants/event-type';
import { LinkCellEditor } from '../components/AgGrid/CellEditors/LinkCellEditor';

export const EventLogs = () => {
  const navigate = useNavigate();

  const convertCamelCaseToUserFriendly = (str: string) =>
    `${str.charAt(0).toUpperCase()}${str.slice(1).replace(/([A-Z])/g, ' $1')}`;

  const convertObjectToString = (obj: object | null, joinString = ', ') =>
    Object.entries(obj ?? {})
      .map(([key, value]) => `${convertCamelCaseToUserFriendly(key)}: ${value}`)
      .join(joinString);

  const columns: ColDef<EventLogApiModel & WithUser>[] = [
    {
      field: 'type',
      colId: 'type.name',
      filter: ArrayFilter,
      sortable: true,
      filterParams: { values: Object.values(EventType) },
    },
    {
      field: 'user',
      colId: 'user.firstName',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      valueGetter: ({ data }) => `${data?.user?.firstName} ${data?.user?.lastName}`,
    },
    {
      field: 'details',
      filter: true,
      sortable: true,
      editable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellEditor: LinkCellEditor,
      cellEditorParams: ({ data }: CustomCellEditorProps<EventLogApiModel>) => ({
        text: 'View details',
        onClick: () => navigate(`${data?.id}`),
      }),
      valueGetter: (params) =>
        Object.entries(params.data?.details ?? {})
          ?.map(
            ([key, value]) =>
              `${convertCamelCaseToUserFriendly(key)}: ${typeof value === 'object' ? `(${convertObjectToString(value)})` : value}`,
          )
          .join(', '),
    },
    {
      field: 'createdAt',
      valueGetter: (params) =>
        params.data?.createdAt ? new Date(params.data.createdAt).toLocaleString('en-ca') : undefined,
      filter: 'agDateColumnFilter',
      sortable: true,
      filterParams: { filterOptions: ['lessThan', 'greaterThan', 'inRange'], maxNumConditions: 1 },
    },
  ];

  const getEventLogs: Parameters<typeof InfniteScrollingGrid<EventLogApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterQuery,
    sortQuery,
  }) => {
    const { data } = await searchEventLogs({
      ...filterQuery,
      types: filterQuery?.type,
      cursor,
      limit,
      sort: sortQuery,
    });
    return data;
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title={'EventLogs'} />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h5">Manage Event Logs</Typography>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid columnDefs={columns} onGetRows={getEventLogs} />
        </Box>
      </Box>
    </Box>
  );
};
